import {
  ChangeEventHandler,
  FC,
  FocusEventHandler,
  MouseEventHandler,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { EyeClosedIcon, EyeOpenIcon, FieldGroup } from 'internalLibrary';
import { useField } from 'formik';
import classes from 'classnames';
import styles from './Input.module.scss';

type InputType =
  | 'text'
  | 'password'
  | 'email'
  | 'tel'
  | 'number'
  | 'search'
  | 'url';

export type InputProps = {
  label: string;
  dataTest?: string;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  name: string;
  className?: string;
  inputClassName?: string;
  disabled?: boolean;
  type?: InputType;
  rightText?: string;
  rightTextBold?: boolean;
  rightTextClassName?: string;
  filter?: (value: string) => string;
  formatter?: (value: string) => string;
  helperText?: string;
  externalError?: string;
};

const Input: FC<InputProps> = (props) => {
  const {
    name,
    className,
    inputClassName,
    label,
    dataTest,
    onChange,
    onBlur,
    onFocus,
    type = 'text',
    disabled = false,
    rightText,
    rightTextBold,
    rightTextClassName,
    filter,
    formatter,
    helperText,
    externalError = '',
  } = props;

  const isPasswordType = type === 'password';
  const [isPasswordHidden, setPasswordHidden] = useState(isPasswordType);
  const [isFocused, setIsFocused] = useState(false);
  const [field, meta, fieldHelpers] = useField(name);
  const [displayValue, setDisplayValue] = useState<string>(field.value);
  const [errorFromOutside, setErrorFromOutside] =
    useState<string>(externalError);

  useEffect(() => {
    setErrorFromOutside(externalError);
  }, [externalError]);

  const handleFocus = useCallback<FocusEventHandler<HTMLInputElement>>(
    (e) => {
      onFocus?.(e);
      setIsFocused(true);
    },
    [onFocus],
  );

  const valueChanged: ChangeEventHandler<HTMLInputElement> = (e) => {
    let value = e.target.value;

    if (filter) value = filter(value);

    fieldHelpers.setValue(value);

    setDisplayValue(value);

    setErrorFromOutside('');

    onChange?.(e);
  };

  const inputBlurred: FocusEventHandler<HTMLInputElement> = (e) => {
    fieldHelpers.setTouched(true);
    setIsFocused(false);

    if (formatter) setDisplayValue((prev) => formatter(prev));

    onBlur?.(e);
  };

  const handlePasswordVisibility: MouseEventHandler = (e) => {
    e.preventDefault();
    setPasswordHidden((prev) => !prev);
  };

  return (
    <FieldGroup
      validationText={
        meta.touched && meta.error ? meta.error : errorFromOutside
      }
      dataTestValidationText={`field-group-validation_${name}`}
      helperText={helperText || ''}
    >
      <div className={classes(styles.container, className)}>
        <input
          {...field}
          type={resolveInputType(type, isPasswordHidden, isFocused)}
          data-test={dataTest}
          data-password={isPasswordType}
          id={name}
          value={displayValue}
          onChange={valueChanged}
          onBlur={inputBlurred}
          onFocus={handleFocus}
          className={classes(
            styles.input,
            ((meta.touched && meta.error) || errorFromOutside) && styles.danger,
            inputClassName,
          )}
          placeholder=" " // NOTE: Used for detecting if input has value
          disabled={disabled}
        />
        {label && (
          <label className={classes(styles.label)} htmlFor={name}>
            {label}
          </label>
        )}
        {isPasswordType && (
          <>
            <button
              className={classes(styles.password, styles.hidden)}
              type="button"
              onMouseDown={handlePasswordVisibility}
            >
              <EyeClosedIcon />
            </button>
            <button
              className={classes(styles.password, styles.visible)}
              type="button"
              onMouseDown={handlePasswordVisibility}
            >
              <EyeOpenIcon />
            </button>
          </>
        )}
        {rightText !== undefined && (
          <div className={styles['right-text-wrapper']}>
            <div
              className={classes(
                styles['right-text'],
                rightTextBold ? styles['right-text-bold'] : undefined,
                rightTextClassName,
              )}
            >
              {rightText}
            </div>
          </div>
        )}
      </div>
    </FieldGroup>
  );
};

export default Input;

const resolveInputType = (
  type: InputType,
  isPasswordHidden: boolean,
  isInputFocused: boolean,
): string => {
  switch (type) {
    case 'password':
      if (isPasswordHidden) {
        return 'password';
      } else {
        return 'text';
      }

    case 'number':
      return isInputFocused ? 'number' : 'text';

    default:
      return type;
  }
};
